import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TALENT_STATUSES,
  TALENT_STATUS_MAP,
  type Potential,
  type Risk,
} from '@goyone/shared';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  Notice,
  PageHead,
  Stat,
} from '../../components/ui';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';
import { api, qs } from '../../lib/api';
import { date, num, plural, WORDS } from '../../lib/format';

/* ==========================================================================
   الصفوف كما يرسلها الخادم
   ========================================================================== */

export interface TalentReviewRow {
  id: string;
  name: string;
  heldOn: string | null;
  status: 'DRAFT' | 'FINAL';
  statusLabel: string;
  statusKind: string;
  statusNote: string;
  writable: boolean;
  notes: string | null;
  assessed: number;
}

export interface AssessmentRow {
  id: string;
  performance: string;
  performanceLabel: string;
  potential: Potential;
  potentialLabel: string;
  potentialKind: string;
  flightRisk: Risk;
  riskLabel: string;
  riskKind: string;
  impactOfLoss: number;
  impactLabel: string;
  box: string;
  boxLabel: string;
  boxKind: string;
  boxAction: string;
  atRisk: boolean;
  actions: string | null;
  notes: string | null;
  employee: {
    id: string;
    name: string;
    code: string | null;
    jobTitle: string | null;
    department: string | null;
  };
}

interface Summary {
  reviews: number;
  units: number;
  partners: number;
  uncovered: number;
  latest: {
    id: string;
    name: string;
    heldOn: string | null;
    assessed: number;
    highPotential: number;
    atRisk: number;
  } | null;
}

interface HighPotentials {
  review: { id: string; name: string; heldOn: string | null } | null;
  reason: string | null;
  rows: AssessmentRow[];
}

type Tab = 'reviews' | 'hipo' | 'partners';

const TABS: readonly { key: Tab; label: string }[] = [
  { key: 'reviews', label: 'جلسات المراجعة' },
  { key: 'hipo', label: 'أصحاب الإمكانات العالية' },
  { key: 'partners', label: 'شركاء الموارد البشرية' },
] as const;

/* ==========================================================================
   الصفحة
   ========================================================================== */

/**
 * إدارة المواهب.
 *
 * ## محوران لا محور
 *
 * الأداءُ والإمكاناتُ شيئان: من يؤدّي اليوم ممتازًا في عمله قد لا يصلح لما
 * فوقه، ومن يؤدّي وسطًا قد يكون في غير موضعه. وخلطُهما هو خطأُ مراجعات
 * المواهب الأشهر، وثمنُه ترقيةُ من كان يُحسن عملَه إلى عملٍ يُسيئه.
 *
 * ## وما لا يقع هنا
 *
 * لا يُرقَّى أحدٌ ولا يُخفَض. الجلسةُ تُنتج قرارًا مكتوبًا في حقّ كلّ اسم،
 * ومن يملك القرارَ يفعله في بابه: الترقيةُ في ملفّ الموظّف، والتدريبُ في
 * التدريب، والخلافةُ في خطط الإحلال.
 */
export function Talent() {
  const [tab, setTab] = useState<Tab>('reviews');

  const summary = useQuery({
    queryKey: ['talent', 'summary'],
    queryFn: () => api<Summary>('/hr/talent/summary'),
  });

  const s = summary.data;

  return (
    <div className="page">
      <PageHead
        title="إدارة المواهب"
        subtitle="من يُحتفظ به ويُستثمر فيه، ومن يخلف من، ومن يُسأل عن كلّ وحدة"
      />

      {summary.isError && (
        <ErrorState
          error={summary.error}
          onRetry={() => void summary.refetch()}
        />
      )}

      {s && (
        <div className="stats">
          <Stat
            label="جلسات المراجعة"
            value={num(s.reviews)}
            note={s.latest ? `آخرُها «${s.latest.name}»` : 'لا جلسةَ اعتُمدت'}
          />
          <Stat
            label="أصحاب الإمكانات العالية"
            value={s.latest ? num(s.latest.highPotential) : '—'}
            note={
              s.latest
                ? `من ${num(s.latest.assessed)} اسمًا في آخر جلسة`
                : 'يُقرَّر في جلسة'
            }
          />
          <Stat
            label="في خطر"
            value={s.latest ? num(s.latest.atRisk) : '—'}
            note="إمكاناتٌ عالية، واحتمالُ تركٍ مرتفع، وفقدُه يُعطّل"
          />
          <Stat
            label="وحداتٌ بلا شريك"
            value={num(s.uncovered)}
            note={`من ${num(s.units)} وحدةً مفعّلة`}
          />
        </div>
      )}

      {s && s.latest && s.latest.atRisk > 0 && (
        <Notice title="أسماءٌ في خطر" icon="alert" kind="danger">
          {plural(s.latest.atRisk, WORDS.employee)} إمكاناتُه عاليةٌ واحتمالُ
          تركه مرتفعٌ وفقدُه يُعطّل عملًا قائمًا. والحديثُ معه اليوم أرخصُ من
          تعيين بديلٍ بعد شهرين.{' '}
          <Link className="link" to={`/hr/talent/reviews/${s.latest.id}`}>
            افتح الجلسة
          </Link>
          .
        </Notice>
      )}

      <div className="tabs" role="tablist" aria-label="أقسام إدارة المواهب">
        {TABS.map((t) => (
          <button
            key={t.key}
            className="tab"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'reviews' && <ReviewsSection />}
      {tab === 'hipo' && <HighPotentialSection />}
      {tab === 'partners' && <PartnersSection />}
    </div>
  );
}

/* ==========================================================================
   الجلسات
   ========================================================================== */

function ReviewsSection() {
  const [status, setStatus] = useState('');
  const [writing, setWriting] = useState(false);

  const query = { status: status || undefined };

  const list = useQuery({
    queryKey: ['talent', 'reviews', query],
    queryFn: () => api<TalentReviewRow[]>(`/hr/talent/reviews${qs(query)}`),
  });

  const rows = list.data ?? [];

  return (
    <div className="card mt-3">
      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <h2 className="card-title" style={{ flex: 1 }}>
          جلسات مراجعة المواهب
        </h2>
        <select
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="">كل الحالات</option>
          {TALENT_STATUSES.map((k) => (
            <option key={k} value={k}>
              {TALENT_STATUS_MAP[k].label}
            </option>
          ))}
        </select>
        <button className="btn btn-primary" onClick={() => setWriting(true)}>
          <Icon name="plus" size={15} />
          جلسة
        </button>
      </div>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {list.data && rows.length === 0 && (
        <EmptyState
          title="لا جلسات"
          text="تُعقد مرّةً أو مرّتين في السنة: يُوضَع فيها كلُّ من يُتحدَّث عنه في مصفوفة الأداء والإمكانات، ويُكتب لكلٍّ ما يُفعل به. وقيمتُها في المقارنة بين جلستين — من تحرّك ومن بقي."
          action={
            <button
              className="btn btn-primary"
              onClick={() => setWriting(true)}
            >
              اعقد أوّل جلسة
            </button>
          }
        />
      )}

      {rows.length > 0 && (
        <div className="table-wrap sticky-head mt-3">
          <table className="table">
            <thead>
              <tr>
                <th>الجلسة</th>
                <th>عُقدت في</th>
                <th>الأسماء</th>
                <th>الحالة</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{r.name}</td>
                  <td className="tiny">{date(r.heldOn ?? '')}</td>
                  <td className="num">{num(r.assessed)}</td>
                  <td>
                    <Badge kind={r.statusKind}>{r.statusLabel}</Badge>
                  </td>
                  <td>
                    <Link
                      className="btn btn-sm"
                      to={`/hr/talent/reviews/${r.id}`}
                    >
                      افتحها
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {writing && <ReviewDialog onClose={() => setWriting(false)} />}
    </div>
  );
}

function ReviewDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [name, setName] = useState('');
  const [heldOn, setHeldOn] = useState(today);
  const [notes, setNotes] = useState('');

  const save = useMutation({
    mutationFn: () =>
      api('/hr/talent/reviews', {
        method: 'POST',
        body: { name, heldOn, notes: notes || null },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['talent'] });
      toast('فُتحت الجلسة.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title="جلسة مراجعة مواهب"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || name.trim().length < 3}
            onClick={() => save.mutate()}
          >
            افتحها
          </button>
        </>
      }
    >
      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="tr-name">
            اسم الجلسة
          </label>
          <input
            id="tr-name"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="مراجعة المواهب — منتصف ٢٠٢٦"
          />
        </div>

        <div className="field">
          <label className="label req" htmlFor="tr-held">
            عُقدت في
          </label>
          <input
            id="tr-held"
            className="input"
            type="date"
            value={heldOn}
            onChange={(e) => setHeldOn(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="tr-notes">
            ملاحظات
          </label>
          <textarea
            id="tr-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="من حضر، وعلى أيّ أساسٍ نُوقشت الأسماء"
          />
        </div>
      </div>
    </Modal>
  );
}

/* ==========================================================================
   أصحاب الإمكانات العالية
   ========================================================================== */

function HighPotentialSection() {
  const list = useQuery({
    queryKey: ['talent', 'hipo'],
    queryFn: () => api<HighPotentials>('/hr/talent/high-potentials'),
  });

  const d = list.data;

  return (
    <div className="card mt-3">
      <h2 className="card-title">أصحاب الإمكانات العالية</h2>

      {/*
        قرارُ جلسةٍ لا وسمٌ يُكتب في ملفّ: وسمٌ يُكتب بيدٍ في ملفّ موظّفٍ
        يبقى سنواتٍ بعد أن تغيّر كلُّ شيء، ولا يُعرف من كتبه ولا متى.
      */}
      <Notice title="من أين يُقرأ" icon="info">
        من آخر جلسةٍ اعتُمدت — لا من وسمٍ يُكتب في ملفّ. والوسمُ يبقى سنواتٍ بعد
        أن يتغيّر كلُّ شيء ولا يُعرف من كتبه، والجلسةُ لها تاريخٌ ومن حضرها وما
        قُرِّر فيها.
      </Notice>

      {list.isError && (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} />
      )}
      {list.isPending && <div className="skeleton" style={{ height: 140 }} />}

      {d?.reason && <EmptyState title="لا جلسةَ اعتُمدت" text={d.reason} />}

      {d?.review && d.rows.length === 0 && (
        <EmptyState
          title="لا أحد"
          text={`لم يُوضَع أحدٌ في «إمكاناتٌ عالية» في «${d.review.name}».`}
        />
      )}

      {d?.review && d.rows.length > 0 && (
        <>
          <p className="tiny mute mt-2">
            من «{d.review.name}» — {date(d.review.heldOn ?? '')}
          </p>
          <div className="table-wrap sticky-head mt-2">
            <table className="table">
              <thead>
                <tr>
                  <th>الاسم</th>
                  <th>الوظيفة</th>
                  <th>الأداء</th>
                  <th>الخانة</th>
                  <th>احتمال الترك</th>
                  <th>ما يُفعل</th>
                </tr>
              </thead>
              <tbody>
                {d.rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      <Link
                        className="link"
                        to={`/hr/employees/${a.employee.id}`}
                      >
                        {a.employee.name}
                      </Link>
                      {a.atRisk && (
                        <div
                          className="tiny"
                          style={{ color: 'var(--danger)' }}
                        >
                          في خطر
                        </div>
                      )}
                    </td>
                    <td className="tiny">{a.employee.jobTitle ?? '—'}</td>
                    <td className="tiny">{a.performanceLabel}</td>
                    <td>
                      <Badge kind={a.boxKind}>{a.boxLabel}</Badge>
                    </td>
                    <td>
                      <Badge kind={a.riskKind}>{a.riskLabel}</Badge>
                    </td>
                    <td className="tiny">{a.actions ?? a.boxAction}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/* ==========================================================================
   شركاء الموارد البشرية
   ========================================================================== */

interface CoverageRow {
  unitId: string;
  name: string;
  kind: string;
  headcount: number;
  partner: { id: string; name: string; code: string | null } | null;
  since: string | null;
  notes: string | null;
}

interface Coverage {
  rows: CoverageRow[];
  uncovered: number;
  load: { partnerId: string; name: string; units: number; headcount: number }[];
}

interface EmployeeLite {
  id: string;
  name: string;
}

function PartnersSection() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editing, setEditing] = useState<CoverageRow | null>(null);

  const coverage = useQuery({
    queryKey: ['talent', 'partners'],
    queryFn: () => api<Coverage>('/hr/talent/partners'),
  });

  const remove = useMutation({
    mutationFn: (unitId: string) =>
      api(`/hr/talent/partners/${unitId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['talent'] });
      toast('رُفع الشريك.');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const d = coverage.data;

  return (
    <div className="card mt-3">
      <h2 className="card-title">شريكُ كلّ وحدة</h2>

      {/*
        تُعرض الوحداتُ كلُّها لا الشركاءُ وحدَهم: الخبرُ في من لا شريكَ له.
        وعبءُ كلّ شريكٍ يُقال معه — شريكٌ على عشر وحداتٍ لا يعرف أيًّا منها.
      */}
      <Notice title="ما يُقاس هنا" icon="info">
        تُعرض الوحداتُ كلُّها: وحدةٌ بلا شريكٍ لا يُسأل عنها أحد. ويُقال مع كلّ
        شريكٍ كم وحدةً يحمل وكم رأسًا فيها — شريكٌ على عشر وحداتٍ لا يعرف أيًّا
        منها.
      </Notice>

      {coverage.isError && (
        <ErrorState
          error={coverage.error}
          onRetry={() => void coverage.refetch()}
        />
      )}
      {coverage.isPending && (
        <div className="skeleton" style={{ height: 160 }} />
      )}

      {d && d.rows.length === 0 && (
        <EmptyState
          title="لا وحدات"
          text="ارسم الهيكلَ التنظيميَّ أوّلًا — الشريكُ يُسنَد إلى وحدةٍ لا إلى قسمٍ في كتالوج."
          action={
            <Link className="btn btn-primary" to="/settings/org-structure">
              الهيكل التنظيمي
            </Link>
          }
        />
      )}

      {d && d.rows.length > 0 && (
        <>
          {d.uncovered > 0 && (
            <Notice title="وحداتٌ بلا شريك" icon="alert" kind="warning">
              {plural(d.uncovered, WORDS.unit)} بلا شريكٍ من الموارد البشرية.
            </Notice>
          )}

          <div className="table-wrap sticky-head mt-3">
            <table className="table">
              <thead>
                <tr>
                  <th>الوحدة</th>
                  <th>من فيها</th>
                  <th>الشريك</th>
                  <th>منذ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r) => (
                  <tr key={r.unitId}>
                    <td>{r.name}</td>
                    <td className="num">{num(r.headcount)}</td>
                    <td className="tiny">
                      {r.partner?.name ?? (
                        <span style={{ color: 'var(--warning)' }}>
                          بلا شريك
                        </span>
                      )}
                    </td>
                    <td className="tiny">{r.since ? date(r.since) : '—'}</td>
                    <td>
                      <div className="row" style={{ gap: 4 }}>
                        <button
                          className="btn btn-sm"
                          onClick={() => setEditing(r)}
                        >
                          {r.partner ? 'غيّره' : 'أسنِد'}
                        </button>
                        {r.partner && (
                          <button
                            className="btn btn-sm btn-ghost"
                            disabled={remove.isPending}
                            onClick={() => remove.mutate(r.unitId)}
                          >
                            ارفعه
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {d.load.length > 0 && (
            <div className="mt-4">
              <h3 className="card-title">عبءُ كلّ شريك</h3>
              <div className="table-wrap mt-2">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الشريك</th>
                      <th>وحدات</th>
                      <th>رؤوس</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.load.map((l) => (
                      <tr key={l.partnerId}>
                        <td>{l.name}</td>
                        <td className="num">{num(l.units)}</td>
                        <td className="num">{num(l.headcount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {editing && (
        <PartnerDialog row={editing} onClose={() => setEditing(null)} />
      )}
    </div>
  );
}

function PartnerDialog({
  row,
  onClose,
}: {
  row: CoverageRow;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const today = new Date().toISOString().slice(0, 10);
  const [partnerId, setPartnerId] = useState(row.partner?.id ?? '');
  const [since, setSince] = useState(row.since ?? today);
  const [notes, setNotes] = useState(row.notes ?? '');

  const staff = useQuery({
    queryKey: ['employees', { status: 'ACTIVE', pageSize: 200 }],
    queryFn: () =>
      api<{ items: EmployeeLite[] }>(
        '/hr/employees?status=ACTIVE&pageSize=200',
      ),
  });

  const brief = useQuery({
    queryKey: ['talent', 'unit-brief', row.unitId],
    queryFn: () =>
      api<{
        headcount: number;
        positions: number;
        openJobs: number;
        openNeeds: number;
        openCases: number;
      }>(`/hr/talent/partners/${row.unitId}/brief`),
  });

  const save = useMutation({
    mutationFn: () =>
      api('/hr/talent/partners', {
        method: 'POST',
        body: {
          orgUnitId: row.unitId,
          partnerId,
          since,
          notes: notes || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['talent'] });
      toast('أُسنِد الشريك.');
      onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal
      title={`شريكُ ${row.name}`}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            disabled={save.isPending || !partnerId}
            onClick={() => save.mutate()}
          >
            احفظ
          </button>
        </>
      }
    >
      {brief.data && (
        <Notice title="ما على شريكها اليوم" icon="clipboard">
          {num(brief.data.headcount)} رأسًا، و{num(brief.data.positions)} وصفًا
          وظيفيًّا، و{num(brief.data.openJobs)} شاغرًا مفتوحًا، و
          {num(brief.data.openNeeds)} احتياجًا تدريبيًّا، و
          {num(brief.data.openCases)} حالةً مفتوحة.
        </Notice>
      )}

      <div className="form-grid mt-3">
        <div className="field span-2">
          <label className="label req" htmlFor="hp-partner">
            الشريك
          </label>
          <select
            id="hp-partner"
            className="select"
            value={partnerId}
            onChange={(e) => setPartnerId(e.target.value)}
          >
            <option value="">— اختر —</option>
            {(staff.data?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            شريكٌ واحدٌ لكلّ وحدة: اثنان يعنيان أنّ أحدًا لا يُسأل.
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="hp-since">
            منذ
          </label>
          <input
            id="hp-since"
            className="input"
            type="date"
            value={since}
            onChange={(e) => setSince(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="hp-notes">
            ملاحظات
          </label>
          <textarea
            id="hp-notes"
            className="input"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>
    </Modal>
  );
}
